import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router"
import * as XLSX from "xlsx"

import { FiArrowLeft } from "react-icons/fi"
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa"
import { RiDeleteBinLine } from "react-icons/ri"
import Button from "react-bootstrap/Button"
import { useVirtualizer } from "@tanstack/react-virtual"
import "./index.scss"

import { ConfirmDialog, Loading } from "components"
import { Text } from "components/Form"
import { integrationSheets, message } from "state_management"
import useT from "utils/hooks/useT"
import {
  applyCellText,
  cellText,
  displayOrder,
  exportRows,
  isCellValid,
  matchingKeys,
  nextSort,
  orderHeaders,
  parseClipboard,
  visibleRows,
} from "./gridUtils"

const BACK_URL = "/setup/integration"
const BACK_STATE = { state: { scrollToAutomatic: true } }
const GO_BACK_CONFIRM_MS = 10000
const EDITABLE = "[contenteditable]"
// Rows rendered beyond the visible ones, so Tab and the arrow keys rarely wait for a render.
const OVERSCAN_ROWS = 10
// A typical row wraps to two or three lines; a close estimate keeps the scroll bar from jumping
// while rows are measured.
const ESTIMATED_ROW_HEIGHT = 80

// Client-side row identity. Never sent to the server.
let lastRowKey = 0
const newRowKey = () => `row-${++lastRowKey}`
const toGridRows = (rows = []) => rows.map((data) => ({ key: newRowKey(), data }))

// Loads the sheet, then hands it to the grid. The grid owns the edit state and is keyed by the
// sheet id, so it always starts from the stored sheet.
export default function DataGridLayout() {
  const { t: tc } = useT("common")
  const dispatch = useDispatch()
  const { sheetId } = useParams()

  const isLoading = useSelector((state) => state.integrationSheets.isLoadingConversionSheet)
  const sheet = useSelector((state) => state.integrationSheets.conversionSheets[sheetId])
  const limsSheets = useSelector((state) => state.integrationSheets.limsSheets)
  const sapSheets = useSelector((state) => state.integrationSheets.sapSheets)

  const sheetType = sheetTypeOf(sheetId, limsSheets, sapSheets)

  useEffect(() => {
    if (!sheetType) {
      // The sheet type is only known from the upload-config lists.
      dispatch(integrationSheets.fetchLimsSheets())
      dispatch(integrationSheets.fetchSapSheets())
    } else if (sheet === undefined) {
      const fetchSheet =
        sheetType === "Sample" ? integrationSheets.fetchLimsConversionSheet : integrationSheets.fetchSapConversionSheet
      dispatch(fetchSheet(sheetId))
    }
  }, [dispatch, sheet, sheetId, sheetType])

  if (isLoading || !sheet || !sheetType) {
    return <Loading title={tc("loadingSheet")} />
  }
  return <ConversionSheetGrid key={sheetId} sheet={sheet} sheetId={sheetId} sheetType={sheetType} />
}

function sheetTypeOf(sheetId, limsSheets, sapSheets) {
  if (limsSheets?.some((s) => s.id === sheetId)) return "Sample"
  if (sapSheets?.some((s) => s.id === sheetId)) return "Project"
  return ""
}

function initGrid(sheet) {
  const rows = toGridRows(sheet.rows)
  return { pristine: rows, rows, touched: new Map(), showErrors: false, sort: null, query: "", filterKeys: null }
}

const markTouched = (touched, rowKey, colKeys) => {
  const next = new Map(touched)
  next.set(rowKey, new Set([...(touched.get(rowKey) || []), ...colKeys]))
  return next
}

// Only the edited rows get new objects, so unchanged rows keep their identity and skip rendering.
function gridReducer(state, action) {
  switch (action.type) {
    case "edit": {
      const { rowKey, colKey, text } = action
      const row = state.rows.find((r) => r.key === rowKey)
      if (!row) return state
      const data = applyCellText(row.data, colKey, text)
      const touched = markTouched(state.touched, rowKey, [colKey])
      if (data[colKey] === row.data[colKey] && (colKey in data) === (colKey in row.data)) {
        return { ...state, touched }
      }
      return { ...state, touched, rows: state.rows.map((r) => (r === row ? { ...r, data } : r)) }
    }
    case "paste": {
      // Pasted rows follow the rows on screen; rows beyond the last one become new rows.
      const { rowKey, colKey, cells, headers, newKeys } = action
      const order = visibleRows(state.rows, state.sort, state.filterKeys)
      const start = order.findIndex((r) => r.key === rowKey)
      const firstCol = headers.findIndex((h) => h.key === colKey)
      if (start === -1 || firstCol === -1) return state
      const updates = new Map()
      const added = []
      // One copy for the whole paste: copying per line made a 20,000-row paste take quadratic time.
      const touched = new Map(state.touched)
      cells.forEach((line, i) => {
        const target = order[start + i] || { key: newKeys[added.length], data: {}, isNew: true }
        if (!order[start + i]) added.push(target)
        let data = updates.get(target.key)?.data || target.data
        const colKeys = []
        line.forEach((text, j) => {
          const header = headers[firstCol + j]
          if (!header) return
          data = applyCellText(data, header.key, text)
          colKeys.push(header.key)
        })
        updates.set(target.key, { ...target, data })
        touched.set(target.key, new Set([...(touched.get(target.key) || []), ...colKeys]))
      })
      const rows = [...state.rows, ...added].map((r) => updates.get(r.key) || r)
      return { ...state, rows, touched }
    }
    case "addRow":
      return { ...state, rows: [...state.rows, { key: action.rowKey, data: {}, isNew: true }] }
    case "deleteRow": {
      const touched = new Map(state.touched)
      touched.delete(action.rowKey)
      return { ...state, touched, rows: state.rows.filter((r) => r.key !== action.rowKey) }
    }
    case "clear":
      return { ...state, rows: [], touched: new Map() }
    case "reset": {
      // The restored rows get new keys, so the search is applied to them again.
      const fresh = initGrid(action.sheet)
      return { ...fresh, sort: state.sort, query: state.query, filterKeys: matchingKeys(fresh.rows, action.headers, state.query) }
    }
    case "search":
      return { ...state, query: action.query, filterKeys: matchingKeys(state.rows, action.headers, action.query) }
    case "sort":
      return { ...state, sort: nextSort(state.sort, action.colKey) }
    case "showErrors":
      return { ...state, showErrors: true }
    default:
      return state
  }
}

const cellElement = (table, rowKey, colIndex) =>
  table?.querySelector(`tr[data-rowkey="${rowKey}"]`)?.querySelectorAll(EDITABLE)[colIndex] || null

function ConversionSheetGrid({ sheet, sheetId, sheetType }) {
  const { t } = useT("messages")
  const { t: tc } = useT("common")
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [grid, update] = useReducer(gridReducer, sheet, initGrid)
  const [useBusinessDays, setUseBusinessDays] = useState(Boolean(sheet.useBusinessDays))
  const [confirmGoBack, setConfirmGoBack] = useState(false)
  const [confirmClear, setConfirmClear] = useState(false)
  const scrollRef = useRef(null)
  const tableRef = useRef(null)
  const orderRef = useRef([])
  const pendingFocus = useRef(null)
  const goBackTimer = useRef(null)

  const headers = useMemo(() => orderHeaders(sheet.headers || [], sheetType), [sheet.headers, sheetType])
  const order = useMemo(() => visibleRows(grid.rows, grid.sort, grid.filterKeys), [grid.rows, grid.sort, grid.filterKeys])
  const isEdited =
    grid.rows !== grid.pristine || useBusinessDays !== Boolean(sheet.useBusinessDays)

  // The React Compiler cannot memoise useVirtualizer's mutable API. This project does not use the
  // compiler, so the warning has no effect here (owner-approved exception, SPEC-069).
  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: order.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    getItemKey: (index) => order[index].key,
    overscan: OVERSCAN_ROWS,
  })
  const items = virtualizer.getVirtualItems()
  const spaceAbove = items.length ? items[0].start : 0
  const spaceBelow = items.length ? virtualizer.getTotalSize() - items[items.length - 1].end : 0

  useEffect(() => () => clearTimeout(goBackTimer.current), [])

  // Focus a cell once its row is rendered; a row outside the rendered window is scrolled to first.
  // A row that is not on screen yet (just added, or hidden by a search being cleared) waits for the
  // next render; after a render it is dropped if it still is not there.
  const applyPendingFocus = useCallback(
    (afterRender) => {
      const target = pendingFocus.current
      if (!target) return
      const el = cellElement(tableRef.current, target.rowKey, target.colIndex)
      if (el) {
        pendingFocus.current = null
        el.focus({ preventScroll: true })
        el.scrollIntoView({ block: "nearest" })
        placeCaret(el, target.caretAtEnd)
        return
      }
      if (target.scrolled) return
      const index = orderRef.current.findIndex((r) => r.key === target.rowKey)
      if (index !== -1) {
        target.scrolled = true
        virtualizer.scrollToIndex(index, { align: "center" })
      } else if (afterRender) {
        pendingFocus.current = null
      }
    },
    [virtualizer]
  )

  const requestFocus = useCallback(
    (rowKey, colIndex, caretAtEnd = true) => {
      pendingFocus.current = { rowKey, colIndex, caretAtEnd, scrolled: false }
      applyPendingFocus(false)
    },
    [applyPendingFocus]
  )

  useLayoutEffect(() => {
    orderRef.current = order
    applyPendingFocus(true)
  })

  const onEdit = useCallback((rowKey, colKey, text) => update({ type: "edit", rowKey, colKey, text }), [])
  const onDelete = useCallback((rowKey) => update({ type: "deleteRow", rowKey }), [])
  const onPaste = useCallback(
    (rowKey, colKey, clipboardText) => {
      const cells = parseClipboard(clipboardText)
      const newKeys = cells.map(newRowKey)
      update({ type: "paste", rowKey, colKey, cells, headers, newKeys })
    },
    [headers]
  )
  // Up/down/Enter move by row index in the on-screen order, so they work past the rendered rows.
  const onMoveRow = useCallback(
    (rowKey, colIndex, step) => {
      const list = orderRef.current
      const target = list[list.findIndex((r) => r.key === rowKey) + step]
      if (!target) return false
      requestFocus(target.key, colIndex)
      return true
    },
    [requestFocus]
  )

  const handleAddRow = () => {
    const rowKey = newRowKey()
    update({ type: "addRow", rowKey })
    requestFocus(rowKey, 0)
  }

  const handleSave = () => {
    const isRowValid = (row) => headers.every((h) => isCellValid(h.key, row.data[h.key]))
    const invalid = displayOrder(grid.rows, grid.sort).find((row) => !isRowValid(row))
    if (invalid) {
      update({ type: "showErrors" })
      if (!order.includes(invalid)) update({ type: "search", query: "", headers })
      requestFocus(invalid.key, Math.max(0, headers.findIndex((h) => !isCellValid(h.key, invalid.data[h.key]))))
      dispatch(message.warning(t("adhoc.fillAllRequiredFields")))
      return
    }
    const save =
      sheetType === "Sample" ? integrationSheets.saveLimsConversionSheet : integrationSheets.saveSapConversionSheet
    dispatch(save(sheetId, grid.rows.map((row) => row.data), useBusinessDays)).then(() =>
      navigate(BACK_URL, BACK_STATE)
    )
  }

  const handleReset = () => {
    update({ type: "reset", sheet, headers })
    setUseBusinessDays(Boolean(sheet.useBusinessDays))
  }

  const handleGoBack = () => {
    if (!isEdited || confirmGoBack) {
      navigate(BACK_URL, BACK_STATE)
      return
    }
    dispatch(message.warning(t("adhoc.unsavedChangesConfirmLeave")))
    setConfirmGoBack(true)
    clearTimeout(goBackTimer.current)
    goBackTimer.current = setTimeout(() => setConfirmGoBack(false), GO_BACK_CONFIRM_MS)
  }

  // The export is the whole sheet in the current sort order; the search only narrows the view.
  const handleExportExcel = () => {
    const workbook = XLSX.utils.book_new()
    const sheetRows = exportRows(headers, displayOrder(grid.rows, grid.sort))
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(sheetRows), "Sheet1")
    XLSX.writeFile(workbook, tc("conversionSheetFilename"))
  }

  return (
    <div className="DataGridLayout-container">
      {confirmClear && (
        <ConfirmDialog
          onConfirm={() => {
            update({ type: "clear" })
            setConfirmClear(false)
          }}
          onCancel={() => setConfirmClear(false)}
        >
          <h4>{tc("confirmClearTable")}</h4>
        </ConfirmDialog>
      )}
      <h3>{tc("automaticImportConversionSheet", { type: sheetType })}</h3>
      <div className="data-grid-config-buttons">
        <div className="data-grid-left-button">
          <Button variant={isEdited && confirmGoBack ? "danger" : "primary"} title={tc("goBack")} onClick={handleGoBack}>
            <FiArrowLeft />
          </Button>
          <div className="data-grid-search">
            <Text
              value={grid.query}
              onChange={(query) => update({ type: "search", query, headers })}
              placeholder={tc("filterByText")}
            />
          </div>
        </div>
        <div className="data-grid-center-buttons">
          <Button variant="secondary" onClick={handleAddRow}>
            {tc("addRow")}
          </Button>
          <Button variant="secondary" onClick={handleReset}>
            {tc("cancelChanges")}
          </Button>
          <Button onClick={handleSave}>{tc("save")}</Button>
          <Button variant="success" onClick={handleExportExcel}>
            {tc("exportToExcel")}
          </Button>
          <label className="business-days-label">
            <input type="checkbox" checked={useBusinessDays} onChange={(e) => setUseBusinessDays(e.target.checked)} />
            <span className="label-text">{tc("leadTimesInWorkDays")}</span>
          </label>
        </div>
        <div className="data-grid-right-button" style={{ visibility: grid.rows.length === 0 ? "hidden" : "" }}>
          <Button variant="danger" onClick={() => setConfirmClear(true)}>
            {tc("clearTable")}
          </Button>
        </div>
      </div>
      <div className="excelGrid-container" ref={scrollRef}>
        <table className="excelGrid" ref={tableRef}>
          <thead>
            <tr>
              {headers.map((header) => (
                <SortableHeader
                  key={header.key}
                  header={header}
                  sort={grid.sort}
                  title={tc("clickToSortBy", { column: header.name })}
                  onSort={() => update({ type: "sort", colKey: header.key })}
                />
              ))}
              <th className="delete-cell" aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {spaceAbove > 0 && <SpacerRow height={spaceAbove} columns={headers.length + 1} />}
            {items.map((item) => {
              const row = order[item.index]
              return (
                <GridRow
                  key={row.key}
                  index={item.index}
                  measureRef={virtualizer.measureElement}
                  row={row}
                  headers={headers}
                  touchedCols={grid.touched.get(row.key)}
                  showErrors={grid.showErrors}
                  onEdit={onEdit}
                  onPaste={onPaste}
                  onDelete={onDelete}
                  onMoveRow={onMoveRow}
                  deleteTitle={tc("deleteThisRow")}
                />
              )
            })}
            {spaceBelow > 0 && <SpacerRow height={spaceBelow} columns={headers.length + 1} />}
            {order.length === 0 && grid.rows.length > 0 && (
              <tr className="no-results">
                <td colSpan={headers.length + 1}>{tc("noSearchResults")}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="add-row-btn-container">
        <Button variant="secondary" onClick={handleAddRow}>
          {tc("addRow")}
        </Button>
        <Button variant="primary" onClick={() => virtualizer.scrollToOffset(0)}>
          {tc("scrollToTop")}
        </Button>
      </div>
    </div>
  )
}

// Stands in for the rows that are not rendered, so the scroll bar matches the whole sheet.
function SpacerRow({ height, columns }) {
  return (
    <tr className="spacer-row" aria-hidden="true" style={{ height }}>
      <td colSpan={columns} />
    </tr>
  )
}

function SortableHeader({ header, sort, title, onSort }) {
  let Icon = FaSort
  if (sort?.key === header.key) Icon = sort.desc ? FaSortDown : FaSortUp
  return (
    <th className={header.bold ? "sortable bold" : "sortable"} title={title} onClick={onSort}>
      <div className="excelGrid-header-content">
        <span className="header-text">{header.name}</span>
        <span className="sort-icon">
          <Icon size={24} />
        </span>
      </div>
    </th>
  )
}

const GridRow = memo(function GridRow({
  index,
  measureRef,
  row,
  headers,
  touchedCols,
  showErrors,
  onEdit,
  onPaste,
  onDelete,
  onMoveRow,
  deleteTitle,
}) {
  return (
    <tr data-rowkey={row.key} data-index={index} ref={measureRef}>
      {headers.map((header, colIndex) => {
        const value = row.data[header.key]
        const invalid = (showErrors || touchedCols?.has(header.key)) && !isCellValid(header.key, value)
        return (
          <EditableCell
            key={header.key}
            rowKey={row.key}
            colKey={header.key}
            colIndex={colIndex}
            value={value}
            invalid={invalid}
            onEdit={onEdit}
            onPaste={onPaste}
            onMoveRow={onMoveRow}
          />
        )
      })}
      <td className="delete-cell">
        {/* Not reachable by Tab, so tabbing moves straight from cell to cell. */}
        <Button title={deleteTitle} variant="danger" onClick={() => onDelete(row.key)} tabIndex={-1}>
          <RiDeleteBinLine />
        </Button>
      </td>
    </tr>
  )
})

// The cell shows its value as plain text through textContent, so nothing is ever parsed as HTML.
function EditableCell({ rowKey, colKey, colIndex, value, invalid, onEdit, onPaste, onMoveRow }) {
  const ref = useRef(null)
  const text = cellText(value)

  useLayoutEffect(() => {
    if (ref.current.textContent !== text) ref.current.textContent = text
  }, [text])

  const handleBlur = (e) => {
    const typed = e.currentTarget.innerText
    // Show the normalised value even when it equals the stored one (e.g. "5 days" → "5").
    e.currentTarget.textContent = cellText(applyCellText({ [colKey]: value }, colKey, typed)[colKey])
    onEdit(rowKey, colKey, typed)
  }

  const handlePaste = (e) => {
    e.preventDefault()
    onPaste(rowKey, colKey, e.clipboardData.getData("text/plain"))
  }

  const handleKeyDown = (e) => {
    if (e.nativeEvent.isComposing) return
    const step = rowStep(e)
    if (step !== 0) {
      e.preventDefault() // never insert a line break, never move the caret
      onMoveRow(rowKey, colIndex, step)
      return
    }
    moveWithinRow(e)
  }

  return (
    <td className={invalid ? "invalid-cell" : undefined}>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onBlur={handleBlur}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
      />
    </td>
  )
}

// Rows to move for a key: Enter/ArrowDown one down, Shift+Enter/ArrowUp one up, otherwise 0.
function rowStep(e) {
  if (e.key === "Enter") return e.shiftKey ? -1 : 1
  if (e.key === "ArrowDown") return 1
  if (e.key === "ArrowUp") return -1
  return 0
}

// Left/right move the caret inside the text and only jump to the next cell at its start or end.
function moveWithinRow(e) {
  const cell = e.currentTarget
  const td = cell.closest("td")
  let target
  let caretAtEnd = true
  if (e.key === "ArrowLeft") {
    if (caretOffset(cell) !== 0) return
    target = td.previousElementSibling?.querySelector(EDITABLE)
  } else if (e.key === "ArrowRight") {
    if (caretOffset(cell) !== cell.textContent.length) return
    target = td.nextElementSibling?.querySelector(EDITABLE)
    caretAtEnd = false
  }
  if (!target) return
  e.preventDefault()
  target.focus()
  placeCaret(target, caretAtEnd)
}

// Caret position as a character offset into `el`, or -1 while a range is selected.
function caretOffset(el) {
  const selection = window.getSelection()
  if (!selection.rangeCount || !selection.isCollapsed) return -1
  const range = document.createRange()
  range.selectNodeContents(el)
  range.setEnd(selection.focusNode, selection.focusOffset)
  return range.toString().length
}

function placeCaret(el, atEnd) {
  const range = document.createRange()
  range.selectNodeContents(el)
  range.collapse(!atEnd)
  const selection = window.getSelection()
  selection.removeAllRanges()
  selection.addRange(range)
}
